import { ListMetadata, Member } from "../api/endpoint.js";
import Bowser from "bowser";
import cx from "clsx";
import {
  WIDGETS_CLASS_NAMESPACE,
  WIDGETS_DATA_ATTRIBUTE_NAMESPACE,
} from "./constants.js";
import type { Elements } from "./elements.js";

export const canUseDOM = !!(
  typeof window !== "undefined" &&
  window.document &&
  window.document.createElement
);

export function getBestName({
  firstName,
  lastName,
}: Pick<Member, "firstName" | "lastName">) {
  return [firstName, lastName].filter(Boolean).join(" ") || null;
}

export function getComparativeReadableDate(
  now: Date,
  then: Date,
  options: { locale: string; timeZone?: string },
): string {
  const locale = options.locale ?? "en-US";
  const timeSince = now.getTime() - then.getTime();
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  // Has it been less than a minute?
  if (timeSince < 60_000) {
    return formatter.format(0, "second"); // Returns "now" / "maintenant" / "jetzt" etc.
  }

  // Has it been less than an hour?
  if (timeSince < 3_600_000) {
    const timePassed = Math.floor(timeSince / 60_000);
    return formatter.format(-timePassed, "minute");
  }

  // Has it been less than a day?
  if (timeSince < 86_400_000) {
    const timePassed = Math.floor(timeSince / 3_600_000);
    return formatter.format(-timePassed, "hour");
  }

  // Has it been less than a week?
  if (timeSince < 604_800_000) {
    const timePassed = Math.floor(timeSince / 86_400_000);
    return formatter.format(-timePassed, "day");
  }

  // Has it been less than a month?
  if (timeSince < 2_592_000_000) {
    const timePassed = Math.floor(timeSince / 604_800_000);
    return formatter.format(-timePassed, "week");
  }

  // Any later?
  return then.toLocaleDateString(locale, {
    timeZone: options.timeZone,
    month: "long",
    day: "numeric",
    // omit year if it's the same as the current year
    year: now.getFullYear() !== then.getFullYear() ? "numeric" : undefined,
  });
}

export function isObjectLike(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

export function isErrorLike(
  value: unknown,
): value is Record<string, unknown> & { message: string } {
  return isObjectLike(value) && typeof value.message === "string";
}

export function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    "then" in value &&
    typeof value.then === "function"
  );
}

export async function parseErrorResponse(
  response: Response,
): Promise<{ message: string; status: number }> {
  try {
    const json = await response.json();
    if (!isObjectLike(json) || typeof json.message !== "string") {
      return {
        status: response.status,
        message: response.statusText,
      };
    }
    return {
      ...json,
      status: response.status,
      message: json.message || response.statusText,
    };
  } catch {
    return {
      status: response.status,
      message: response.statusText,
    };
  }
}

export function namespaceClassNames(
  ...classNames: (string | undefined | null | boolean)[]
): string {
  return classNames
    .filter(Boolean)
    .map((className) => `${WIDGETS_CLASS_NAMESPACE}-${className}`)
    .join(" ");
}

interface CommonDomArgs {
  className?: string | undefined | null;
  dataAttributes?: Record<string, string | boolean | undefined | null>;
}

interface WidgetRootDomArgs {
  isWidgetRoot: true;
  widgetId: string;
  elementId?: never;
  widgetState: WidgetRootState;
}

interface WidgetElementDomArgs {
  isWidgetRoot?: false;
  widgetId?: string;
  elementId?: keyof Elements;
  widgetState?: never;
  className: string | undefined | null;
}

type DomArgs = CommonDomArgs & (WidgetRootDomArgs | WidgetElementDomArgs);

type DataAttributeProps = {
  [K in `data-${string}`]?: string | boolean;
};

type DomProps = {
  className: string | undefined;
} & DataAttributeProps;

export function getDomProps(args: DomArgs) {
  let {
    className,
    dataAttributes,
    isWidgetRoot = false,
    widgetId,
    elementId,
    widgetState,
    ...passthroughProps
  } = args;

  const elementClassName = elementId
    ? (() => {
        // TODO: Overrides for specific elements are mostly here for backwards
        // compatibility. They should be removed in the next major version.
        if (
          elementId === "primaryButton" ||
          elementId === "secondaryButton" ||
          elementId === "destructiveButton"
        ) {
          return namespaceClassNames("button");
        }

        if (elementId === "iconButton") {
          return namespaceClassNames("button", "icon-button");
        }

        if (
          elementId === "primaryMenuItem" ||
          elementId === "destructiveMenuItem"
        ) {
          return namespaceClassNames("menu-item");
        }

        if (elementId === "textfield") {
          return namespaceClassNames(elementId, "text-field");
        }

        return namespaceClassNames(fastKebabCase(elementId));
      })()
    : undefined;

  const props: DomProps = {
    ...passthroughProps,
    className: cx(
      className,
      isWidgetRoot && namespaceClassNames("widget"),
      elementClassName,
    ),
  };

  if (widgetId) {
    props[`data-${WIDGETS_DATA_ATTRIBUTE_NAMESPACE}-widget-id`] = widgetId;
  }

  if (isWidgetRoot) {
    widgetState ??= "resolved";
    props[`data-${WIDGETS_DATA_ATTRIBUTE_NAMESPACE}-widget`] = true;
    props[`data-${WIDGETS_DATA_ATTRIBUTE_NAMESPACE}-widget-state`] =
      widgetState;
  }

  if (dataAttributes) {
    for (const [key, value] of Object.entries(dataAttributes)) {
      if (value != null) {
        const parts = [widgetId, elementId, key]
          .filter((v) => v != null)
          .map(fastKebabCase)
          .join("-");
        if (parts) {
          props[`data-${WIDGETS_DATA_ATTRIBUTE_NAMESPACE}-${parts}`] = value;
        }
      }
    }
  }

  if (elementId) {
    props[`data-${WIDGETS_DATA_ATTRIBUTE_NAMESPACE}-element`] =
      fastKebabCase(elementId);
  }

  return props;
}

/**
 * Opt for simplicity and performance over capturing edge cases. Input strings
 * are expected to only be alphabetic.
 */
function fastKebabCase(alphaString: string) {
  return alphaString.replace(/([A-Z])/g, "-$1").toLowerCase();
}

export function parseUserAgent(userAgent?: string | null) {
  const browser = Bowser.getParser(userAgent ?? "");
  const browserName = browser.getBrowserName().replace(/^Mobile\s*/i, "");
  const osName = browser.getOSName();
  const pretty = [browserName, osName].filter(Boolean).join(" on ");

  return { pretty, isMobile: browser.getPlatformType() === "mobile" };
}

export function getUserLocation(
  location?: {
    cityName: string;
    countryISOCode: string;
  } | null,
  ipAddress?: string | null,
) {
  if (location) {
    return (
      [location.cityName, location.countryISOCode].filter(Boolean).join(", ") ||
      "Unknown location"
    );
  }

  if (ipAddress) {
    return ipAddress;
  }

  return "Unknown location";
}

export function unreachable(value: never): never {
  throw new TypeError(`Unreachable code: ${value}`);
}

export function pluralize(word = "", amount = 0, showAmount = true): string {
  return `${showAmount ? `${amount} ` : ""}${word}${amount === 1 ? "" : "s"}`;
}

// Props that all widgets should accept.
export interface WidgetRootDomProps {
  className?: string;
}

export type WidgetRootState = "loading" | "error" | "resolved";

// The API response previously returned an object with the `listMetadata`
// property where `before` and `after` fields were not strictly required.
type Pagination = Nullable<{ before: string; after: string }>;
type DeprecatedListMetadataResponse = {
  listMetadata: Pagination;
};
type NewListMetadataResponse = {
  list_metadata: Pagination;
};

export function getListMetadata<
  T extends DeprecatedListMetadataResponse | NewListMetadataResponse,
>(responseObject: T | undefined | null): ListMetadata {
  const listMetadata =
    responseObject && "listMetadata" in responseObject
      ? responseObject.listMetadata
      : responseObject?.list_metadata;

  return {
    before: listMetadata?.before ?? null,
    after: listMetadata?.after ?? null,
  };
}

export type Nullable<T> = {
  [K in keyof T]?: T[K] | null;
};
/**
 * Opens the Admin Portal via form submission instead of URL query parameters.
 * This keeps the API token in the request body rather than the URL,
 * reducing exposure in server logs, browser history, and referrer headers.
 */
export function openAdminPortalViaForm(
  link: string,
  options: { target?: string; rel?: string } = {},
): void {
  let url: URL;
  try {
    url = new URL(link);
  } catch {
    throw new TypeError("Invalid URL");
  }

  let { target, rel } = options;
  const apiToken = url.searchParams.get("apiToken");
  // clear the apiToken from the URL search params to avoid exposing it to the
  // browser console
  url.searchParams.delete("apiToken");

  const formAction = url.toString();
  const form = document.createElement("form");
  form.method = "POST";
  form.action = formAction;
  if (target) {
    form.target = target;
  }

  if (!rel && target === "_blank") {
    rel = "noopener noreferrer";
  }

  if (rel) {
    form.rel = rel;
  }

  const tokenInput = document.createElement("input");
  tokenInput.type = "hidden";
  tokenInput.name = "apiToken";
  tokenInput.value = apiToken ?? "";
  form.appendChild(tokenInput);

  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
}
